const TRANSITION_DURATION = 650;
const SWIPE_THRESHOLD = 50;

const initPlanningSlider = () => {
  const sections = document.querySelectorAll("[data-planning-section]");

  sections.forEach((section) => {
    const sliderEl = section.querySelector(".planning__slider");
    const wrapperEl = section.querySelector(".planning__wrapper");
    const initialSlides = Array.from(section.querySelectorAll(".planning__slide"));
    const filters = Array.from(section.querySelectorAll("[data-planning-filter]"));
    const prevEl = section.querySelector(".planning__btn--prev");
    const nextEl = section.querySelector(".planning__btn--next");
    const navEls = Array.from(section.querySelectorAll(".planning__navs"));

    if (!sliderEl || !wrapperEl || initialSlides.length === 0) return;

    const slideTemplates = initialSlides.map((slide) => ({
      rooms: slide.dataset.rooms,
      element: slide.cloneNode(true),
    }));
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let slides = [];
    let activeIndex = 0;
    let transitionTimer;
    let filterTransitionTimer;
    let isTransitioning = false;
    let pointerStartX = null;

    const updateNavigationVisibility = () => {
      const shouldHide = slides.length <= 1;

      navEls.forEach((navEl) => {
        navEl.hidden = shouldHide;
      });
    };

    const syncActiveFilter = () => {
      const activeRoom = slides[activeIndex]?.dataset.rooms;

      filters.forEach((filter) => {
        const isActive = filter.dataset.planningFilter === activeRoom;

        filter.classList.toggle("is-active", isActive);
        filter.setAttribute("aria-pressed", isActive ? "true" : "false");
      });
    };

    const getSlidePosition = (index, direction = 0) => {
      if (index === activeIndex) return "active";
      if (index === (activeIndex + 1) % slides.length) return "next";
      if (index === (activeIndex - 1 + slides.length) % slides.length) return "prev";

      const forwardDistance = (index - activeIndex + slides.length) % slides.length;
      const backwardDistance = (activeIndex - index + slides.length) % slides.length;

      if (forwardDistance === backwardDistance) {
        return direction > 0 ? "hidden-prev" : "hidden-next";
      }

      return forwardDistance < backwardDistance ? "hidden-next" : "hidden-prev";
    };

    const updateSlides = (animate = true, direction = 0, updateFilter = true) => {
      if (!animate) {
        wrapperEl.classList.add("is-planning-initializing");
      }

      slides.forEach((slide, index) => {
        const isActive = index === activeIndex;

        slide.dataset.planningPosition = getSlidePosition(index, direction);
        slide.classList.toggle("is-planning-active", isActive);
        slide.setAttribute("aria-hidden", isActive ? "false" : "true");
        slide.toggleAttribute("inert", !isActive);
      });

      if (updateFilter) {
        syncActiveFilter();
      }

      if (!animate) {
        wrapperEl.getBoundingClientRect();
        requestAnimationFrame(() => {
          wrapperEl.classList.remove("is-planning-initializing");
        });
      }
    };

    const syncCardHeight = () => {
      wrapperEl.classList.add("is-planning-measuring");
      wrapperEl.style.removeProperty("--planning-card-height");
      const cards = slides.map((slide) => slide.querySelector(".planning__card")).filter(Boolean);
      const maxHeight = Math.max(0, ...cards.map((card) => card.scrollHeight));

      if (maxHeight > 0) {
        wrapperEl.style.setProperty("--planning-card-height", `${Math.ceil(maxHeight)}px`);
      }

      wrapperEl.classList.remove("is-planning-measuring");
    };

    const moveTo = (nextIndex) => {
      if (slides.length <= 1 || isTransitioning) return;

      const targetIndex = (nextIndex + slides.length) % slides.length;
      const direction = targetIndex === (activeIndex + 1) % slides.length ? 1 : -1;
      const incomingIndex = direction > 0
        ? (targetIndex + 1) % slides.length
        : (targetIndex - 1 + slides.length) % slides.length;
      const incomingPosition = direction > 0 ? "hidden-next" : "hidden-prev";
      const incomingSlide = slides[incomingIndex];

      isTransitioning = true;

      if (incomingSlide?.dataset.planningPosition.startsWith("hidden")
        && incomingSlide.dataset.planningPosition !== incomingPosition) {
        wrapperEl.classList.add("is-planning-initializing");
        incomingSlide.dataset.planningPosition = incomingPosition;
        wrapperEl.getBoundingClientRect();
        wrapperEl.classList.remove("is-planning-initializing");
      }

      requestAnimationFrame(() => {
        activeIndex = targetIndex;
        updateSlides(true, direction, false);
        clearTimeout(filterTransitionTimer);
        filterTransitionTimer = window.setTimeout(
          syncActiveFilter,
          reducedMotion.matches ? 0 : TRANSITION_DURATION / 2,
        );
        clearTimeout(transitionTimer);
        transitionTimer = window.setTimeout(() => {
          isTransitioning = false;
        }, reducedMotion.matches ? 0 : TRANSITION_DURATION);
      });
    };

    const buildSlides = (initialRoom) => {
      clearTimeout(transitionTimer);
      clearTimeout(filterTransitionTimer);
      isTransitioning = false;
      slides = slideTemplates
        .map((slide, index) => {
          const slideEl = slide.element.cloneNode(true);

          slideEl.hidden = false;
          slideEl.dataset.planningIndex = String(index);
          slideEl.classList.remove("is-planning-active");

          return slideEl;
        });

      const initialIndex = slides.findIndex((slide) => slide.dataset.rooms === initialRoom);

      activeIndex = initialIndex >= 0 ? initialIndex : 0;

      sliderEl.hidden = slides.length === 0;
      wrapperEl.replaceChildren(...slides);
      updateNavigationVisibility();

      if (slides.length === 0) return;

      updateSlides(false);
      syncCardHeight();
      requestAnimationFrame(syncCardHeight);

      slides.forEach((slide) => {
        slide.querySelectorAll("img").forEach((image) => {
          if (!image.complete) {
            image.addEventListener("load", syncCardHeight, { once: true });
          }
        });
      });
    };

    const showRoom = (room) => {
      const targetIndex = slides.findIndex((slide) => slide.dataset.rooms === room);

      if (targetIndex < 0 || targetIndex === activeIndex) return;

      clearTimeout(transitionTimer);
      clearTimeout(filterTransitionTimer);
      isTransitioning = false;
      activeIndex = targetIndex;
      updateSlides(false);
    };

    const handlePointerEnd = (event) => {
      if (pointerStartX === null) return;

      const distance = event.clientX - pointerStartX;
      pointerStartX = null;

      if (Math.abs(distance) < SWIPE_THRESHOLD) return;

      moveTo(activeIndex + (distance < 0 ? 1 : -1));
    };

    prevEl?.addEventListener("click", () => moveTo(activeIndex - 1));
    nextEl?.addEventListener("click", () => moveTo(activeIndex + 1));
    sliderEl.addEventListener("pointerdown", (event) => {
      if (event.button !== 0) return;
      pointerStartX = event.clientX;
    });
    sliderEl.addEventListener("pointerup", handlePointerEnd);
    sliderEl.addEventListener("pointercancel", () => {
      pointerStartX = null;
    });
    sliderEl.addEventListener("keydown", (event) => {
      if (event.key === "ArrowLeft") moveTo(activeIndex - 1);
      if (event.key === "ArrowRight") moveTo(activeIndex + 1);
    });

    filters.forEach((filter) => {
      filter.addEventListener("click", () => {
        if (!filter.disabled) {
          showRoom(filter.dataset.planningFilter);
        }
      });
    });

    let observedWidth = sliderEl.clientWidth;
    const resizeObserver = typeof ResizeObserver === "function"
      ? new ResizeObserver(([entry]) => {
        const nextWidth = Math.round(entry.contentRect.width);

        if (nextWidth === observedWidth) return;

        observedWidth = nextWidth;
        syncCardHeight();
      })
      : null;

    resizeObserver?.observe(sliderEl);
    buildSlides(section.dataset.initialRoom || "1");

    if (document.fonts?.ready) {
      document.fonts.ready.then(syncCardHeight);
    }

    window.addEventListener("pagehide", () => {
      clearTimeout(transitionTimer);
      clearTimeout(filterTransitionTimer);
      resizeObserver?.disconnect();
    }, { once: true });
  });
};

export default initPlanningSlider;
